import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, MetricTile, Money, Segmented, Skeleton, SkeletonRegion } from '../../../components/ui';
import api from '../../../services/api';
import type { Order } from '../../../types/Order';
import type { RestaurantRecord } from '../apiTypes';
import { useRequest } from '../useRequest';

const RANGES = ['today', 'yesterday', 'last7days', 'last30days', 'all'] as const;
type Range = (typeof RANGES)[number];

const isRange = (v: string | null): v is Range => RANGES.some((r) => r === v);

const bounds = (range: Range): { from?: string; to?: string } => {
    const now = new Date();
    const day = (offset: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    switch (range) {
        case 'today':
            return { from: day(0).toISOString() };
        case 'yesterday':
            return { from: day(-1).toISOString(), to: day(0).toISOString() };
        case 'last7days':
            return { from: day(-6).toISOString() };
        case 'last30days':
            return { from: day(-29).toISOString() };
        default:
            return {};
    }
};

/** Paid and not cancelled: the only orders that count as money taken. */
const summarize = (orders: Pick<Order, 'status' | 'paymentStatus' | 'totalAmount' | 'orderItems'>[]) => {
    const counted = orders.filter((o) => o.paymentStatus === 'paid' && o.status !== 'cancelled');
    const revenue = counted.reduce((sum, o) => sum + Number(o.totalAmount), 0);
    const items = counted.reduce((sum, o) => sum + o.orderItems.reduce((n, i) => n + i.quantity, 0), 0);
    return { revenue, orders: counted.length, average: counted.length ? revenue / counted.length : 0, items };
};

export const LocationStats = ({ locationId, restaurant }: { locationId: string; restaurant: RestaurantRecord }) => {
    const { t } = useTranslation('locations');
    const [params, setParams] = useSearchParams();
    const requested = params.get('range');
    const range: Range = isRange(requested) ? requested : 'today';
    const setRange = (next: Range) =>
        setParams(
            (prev) => {
                const p = new URLSearchParams(prev);
                p.set('range', next);
                return p;
            },
            { replace: true }
        );

    const { data, state, retry } = useRequest(`stats:${locationId}:${range}`, () =>
        api
            .get<Order[]>('/orders', { params: { locationId, status: 'all', ...bounds(range) } })
            .then((r) => summarize(r.data))
    );

    const money = (amount: number) => <Money amount={amount} symbol={restaurant.currencySymbol} position={restaurant.currencySymbolPosition} />;

    return (
        <section aria-labelledby="stats-heading">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 id="stats-heading" className="text-title text-strong">
                    {t('stats.title')}
                </h2>
                <Segmented
                    aria-label={t('stats.range')}
                    value={range}
                    onChange={setRange}
                    options={RANGES.map((r) => ({ value: r, label: t(`stats.ranges.${r}`) }))}
                />
            </div>
            {state === 'error' ? (
                <div role="alert">
                    <Card className="flex flex-wrap items-center gap-3 text-small">
                    {t('stats.failed')}
                    <Button size="sm" variant="outline" onClick={retry}>
                        {t('common:ui.retry')}
                    </Button>
                    </Card>
                </div>
            ) : state === 'loading' || !data ? (
                <SkeletonRegion>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        {Array.from({ length: 4 }, (_, i) => (
                            <Skeleton key={i} className="h-24" />
                        ))}
                    </div>
                </SkeletonRegion>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <MetricTile label={t('stats.revenue')} value={money(data.revenue)} />
                        <MetricTile label={t('stats.orders')} value={data.orders} />
                        <MetricTile label={t('stats.average')} value={money(data.average)} />
                        <MetricTile label={t('stats.items')} value={data.items} />
                    </div>
                    <p className="mt-3 text-caption text-muted">{t('stats.note')}</p>
                </>
            )}
        </section>
    );
};
